import React, {FC, useState, useEffect} from "react"

import {getMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import {getErrorMessage, getErrorValues} from "@irati/utils/lang/formatError"
import salesService from "@irati/sales/services/sales"
import {InputRenewalUpsellContact} from "@irati/types"
import buService from "@irati/billing/services/bu"
import authentication from "@irati/auth"
import adminTracking from "@irati-tracking/admin"

import SelectProviderModal from "./SelectProviderModal"
import CustomProviderModal from "./CustomProviderModal"
import KeyTermsModal from "./KeyTermsModal"
import UpdateContactDetailsModal from "./UpdateContactDetailsModal"
import SuccessResponseModal from "./SuccessResponseModal"
import ErrorResponseModal from "./ErrorResponseModal"
import IntegrationChecklist from "./IntegrationChecklist"
import LaunchInvitationsModal, {LaunchTime} from "./LaunchInvitationsModal"
import GenerateSetupLinkModal from "./GenerateSetupLinkModal"
import LoadingResponseModal from "./LoadingResponseModal"

export const DSYNC_INTEGRATION = "People Directory Integration With 1 Month Discount"

interface Props extends InjectIntl {
  showUpsell: boolean
  finishSetup: boolean
  onClose: () => void
}

const HRISUpsell: FC<Props> = ({showUpsell, finishSetup, onClose, formatMessage}) => {
  const [showProviderModal, setShowProviderModal] = useState(false)
  const [showCustomProviderModal, setShowCustomProviderModal] = useState(false)
  const [showKeyTermsModal, setShowKeyTermsModal] = useState(false)
  const [showContactDetailsModal, setShowContactDetailsModal] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [showLoadingModal, setShowLoadingModal] = useState(false)
  const [showChecklist, setChecklist] = useState(false)
  const [showLaunchModal, setShowLaunchModal] = useState(false)
  const [showGenerateLinkModal, setShowGenerateLinkModal] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string>()
  const [launchTime, setLaunchTime] = useState<LaunchTime>()
  const [submit, setSubmit] = useState<Promise<any> | null>(null)

  const handleOnCloseUpsell = () => {
    setShowProviderModal(false)
    onClose()
  }

  const handleOnCloseSetup = () => {
    setChecklist(false)
    onClose()
  }

  const handleAcceptUpsell = (contactDetails: InputRenewalUpsellContact) => {
    const businessId = authentication.business().uuid
    setShowLoadingModal(true)

    const submitted = buService
      .getProductOptions({businessId})
      .then(({newProducts, platformBillingPeriod}) => {
        const product = newProducts?.find(({name}) => name === DSYNC_INTEGRATION)
        const params = {
          productsAdded: [
            {
              id: product?.id,
              name: product?.name,
              recurringChargeId: product?.recurringChargeId,
              costPerSeatMonth: product?.pricePerSeatPerMonth,
              uncounted: product?.uncounted,
              discount: product?.discount
            }
          ],
          productsRemoved: [],
          productsUpdated: [],
          seats: 0,
          signatureContact: contactDetails,
          platformBillingPeriod: platformBillingPeriod
        }
        salesService
          .acceptRenewalUpsell(params)
          .then(() => {
            setShowLoadingModal(false)
            adminTracking.hrisUpsellSetup("admin_dysnc_purchase_confirm", {action: "contract_complete"})
            setShowSuccessModal(true)
          })
          .catch((err: PBError) => {
            setShowLoadingModal(false)
            const message = getMessage(formatMessage, getErrorMessage(err), getErrorValues(err))
            setErrorMsg(message)
            setShowErrorModal(true)
            adminTracking.hrisUpsellSetup("admin_dysnc_purchase_error", {action: "purchase_error"})
          })
      })
      .catch((err: PBError) => {
        setShowLoadingModal(false)
        const message = getMessage(formatMessage, getErrorMessage(err), getErrorValues(err))
        setErrorMsg(message)
        setShowErrorModal(true)
      })

    setSubmit(submitted)
  }

  const handleLaunchTimeOnNext = (time: LaunchTime) => {
    setLaunchTime(time)
    setShowGenerateLinkModal(true)
  }

  useEffect(() => {
    if (showUpsell) {
      setShowProviderModal(true)
      setErrorMsg("")
    }
  }, [showUpsell])

  useEffect(() => {
    if (finishSetup) {
      setChecklist(true)
    }
  }, [finishSetup])

  return (
    <>
      <SelectProviderModal
        show={showProviderModal}
        onClose={handleOnCloseUpsell}
        onNotFound={() => setShowCustomProviderModal(true)}
        onNext={() => setShowKeyTermsModal(true)}
      />
      <CustomProviderModal show={showCustomProviderModal} onClose={() => setShowCustomProviderModal(false)} />
      <KeyTermsModal
        show={showKeyTermsModal}
        onClose={() => setShowKeyTermsModal(false)}
        onNext={() => setShowContactDetailsModal(true)}
      />
      <UpdateContactDetailsModal
        show={showContactDetailsModal}
        submit={submit}
        onClose={() => setShowContactDetailsModal(false)}
        onNext={handleAcceptUpsell}
      />
      <LoadingResponseModal show={showLoadingModal} onClose={() => setShowLoadingModal(false)} />
      <SuccessResponseModal show={showSuccessModal} onClose={() => setShowSuccessModal(false)} />
      <ErrorResponseModal show={showErrorModal} error={errorMsg} onClose={() => setShowErrorModal(false)} />
      <IntegrationChecklist show={showChecklist} onClose={handleOnCloseSetup} onNext={() => setShowLaunchModal(true)} />
      <LaunchInvitationsModal show={showLaunchModal} onClose={() => setShowLaunchModal(false)} onNext={handleLaunchTimeOnNext} />
      <GenerateSetupLinkModal show={showGenerateLinkModal} launchTime={launchTime} onClose={() => setShowGenerateLinkModal(false)} />
    </>
  )
}

export default injectIntl(HRISUpsell)
